import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Upload, Camera, Check, ArrowRight, Star, Quote, Sparkles, Package } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { HowItWorksSection } from "@/components/HowItWorksSection";
import endlessStorageLogo from "@/assets/endless-storage-logo.png";
import heroImage from "@/assets/hero-box-scanning.jpg";
import { CameraCapture } from "@/components/CameraCapture";

export default function BoxAnalyzer() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [boxNumber, setBoxNumber] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const qrBoxNumber = searchParams.get('boxNumber');
    if (qrBoxNumber) {
      setBoxNumber(qrBoxNumber);
    }
    
    const sourceParam = searchParams.get('source');
    if (sourceParam) {
      console.log('[BoxAnalyzer] Source attribution:', sourceParam);
    }
  }, [searchParams]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
  };

  const handleCameraCapture = (imageData: string) => {
    fetch(imageData)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], "camera-capture.jpg", { type: "image/jpeg" });
        setSelectedFile(file);
      });
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast({
        title: "No Photo",
        description: "Please take or upload a photo of your box first.",
        variant: "destructive",
      });
      return;
    }

    const tempSessionId = crypto.randomUUID();
    
    navigate(`/try/results/${tempSessionId}`, {
      state: {
        analyzing: true,
        imageFile: selectedFile,
        boxNumber: boxNumber,
        sourceCode: searchParams.get('source') || undefined
      }
    });
  };

  return (
    <div className="min-h-screen bg-[hsl(210,20%,98%)]">
      {/* Minimal Header */}
      <header className="absolute top-0 left-0 right-0 z-10">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <a 
              href="https://endless-storage.com"
              className="hover:opacity-80 transition-opacity"
            >
              <img src={endlessStorageLogo} alt="Endless Storage" className="h-9" />
            </a>
            <Button 
              variant="ghost" 
              className="text-foreground/80 hover:text-foreground font-medium"
              onClick={() => navigate('/auth')}
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Split Layout */}
      <div className="pt-20 lg:pt-24 pb-8 lg:pb-16 flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-8 container mx-auto px-4 lg:px-6">
        {/* Left Side - Hero Image (Desktop only) */}
        <div className="hidden lg:block lg:w-[55%] relative h-[580px] rounded-3xl overflow-hidden shadow-md">
          <img 
            src={heroImage} 
            alt="Person organizing moving boxes with phone" 
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[hsl(210,20%,98%)]/10" />
        </div>

        {/* Right Side - Content Card */}
        <div className="flex-1 flex items-center justify-center lg:py-0">
          <div className="w-full max-w-md">
            {/* Floating Card */}
            <div className="bg-background rounded-2xl lg:rounded-3xl shadow-xl lg:shadow-2xl shadow-foreground/5 p-6 lg:p-10">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary mb-4 lg:mb-6">
                <Package className="h-3.5 w-3.5" />
                <span className="text-xs font-semibold tracking-wide uppercase">Free Tool</span>
              </div>

              {/* Headline */}
              <h1 className="text-2xl lg:text-4xl font-bold tracking-tight text-foreground mb-2 lg:mb-3">
                Know What's In Every Box
              </h1>
              
              <p className="text-muted-foreground mb-6 lg:mb-8 leading-relaxed text-sm lg:text-base">
                Snap a photo before you seal. Find anything instantly on moving day.
              </p>

              {boxNumber && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-sm font-medium mb-4 lg:mb-6">
                  Box #{boxNumber}
                </div>
              )}

              {/* Feature List */}
              <div className="space-y-2.5 lg:space-y-3 mb-6 lg:mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-primary" />
                  </div>
                  <span className="text-sm text-foreground/80">Instant item recognition</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-primary" />
                  </div>
                  <span className="text-sm text-foreground/80">Search across all your boxes</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-primary" />
                  </div>
                  <span className="text-sm text-foreground/80">No signup required</span>
                </div>
              </div>

              {/* Upload Actions */}
              <div className="space-y-3 mb-4 lg:mb-6">
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1 h-11 lg:h-12 rounded-xl border-border/60 hover:border-primary/40 hover:bg-primary/5 transition-all"
                    onClick={() => setCameraOpen(true)}
                  >
                    <Camera className="mr-2 h-4 w-4" />
                    Take Photo
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" className="flex-1">
                    <Button 
                      variant="outline" 
                      className="w-full h-11 lg:h-12 rounded-xl border-border/60 hover:border-primary/40 hover:bg-primary/5 transition-all cursor-pointer" 
                      asChild
                    >
                      <span>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload
                      </span>
                    </Button>
                  </label>
                </div>

                {selectedFile && (
                  <div className="flex items-center gap-2 text-sm text-primary font-medium bg-primary/5 rounded-lg px-3 py-2">
                    <Check className="h-4 w-4" />
                    {selectedFile.name}
                  </div>
                )}
              </div>

              {/* Primary CTA */}
              <Button 
                size="lg" 
                className="w-full h-12 lg:h-14 rounded-xl text-base font-semibold bg-[hsl(142,71%,45%)] hover:bg-[hsl(142,71%,40%)] hover:animate-shake text-white shadow-lg shadow-[hsl(142,71%,45%)]/25 transition-all"
                onClick={handleSubmit}
                disabled={!selectedFile}
              >
                Scan My Box
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-3 lg:mt-4">
                Results in ~15 seconds • 100% free
              </p>
            </div>

            {/* Social Proof */}
            <div className="mt-6 lg:mt-8 text-center">
              <div className="flex items-center justify-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-amber-400 text-lg">★</span>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                Used by <span className="font-semibold text-foreground">1,200+ movers</span> in NYC
              </p>
            </div>
          </div>
        </div>

        {/* Mobile Hero Image - Shows AFTER content on small screens */}
        <div className="lg:hidden w-full">
          <div className="rounded-2xl overflow-hidden shadow-md">
            <img 
              src={heroImage} 
              alt="Person organizing moving boxes" 
              className="w-full h-56 object-cover object-top"
            />
          </div>
        </div>
      </div>

      {/* How It Works - Simplified Layout */}
      <HowItWorksSection />

      {/* Statistics Bar */}
      <div className="border-y bg-muted/30">
        <div className="container mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-foreground">50K+</p>
              <p className="text-sm text-muted-foreground mt-1">Boxes organized</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-foreground">1,200+</p>
              <p className="text-sm text-muted-foreground mt-1">Happy movers</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-foreground">15 sec</p>
              <p className="text-sm text-muted-foreground mt-1">Average scan</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-foreground">98%</p>
              <p className="text-sm text-muted-foreground mt-1">Accuracy</p>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="container mx-auto px-6 py-24">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-16 tracking-tight">
            What Movers Say
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: "I took photos of 23 boxes as I packed. On moving day I searched 'winter coat' and found it immediately.",
                author: "Sarah M.",
                location: "Brooklyn"
              },
              {
                quote: "We moved from a 2BR to storage while renovating. Knowing exactly what was in each box made it so much easier.",
                author: "Michael R.",
                location: "Upper East Side"
              },
              {
                quote: "My movers loved it. They just scanned the QR codes to know which room each box should go to.",
                author: "Jennifer K.",
                location: "Chelsea"
              }
            ].map((testimonial, i) => (
              <div key={i} className="bg-background rounded-2xl p-6 shadow-sm border border-border/50">
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <span key={j} className="text-amber-400">★</span>
                  ))}
                </div>
                <p className="text-foreground/80 text-sm leading-relaxed mb-4">
                  "{testimonial.quote}"
                </p>
                <p className="text-xs text-muted-foreground">
                  — {testimonial.author}, {testimonial.location}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="container mx-auto px-6 pb-24">
        <div className="max-w-xl mx-auto text-center">
          <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-3xl p-10 border border-primary/10 flex flex-col items-center">
            <h2 className="text-2xl font-bold mb-3">Ready to Track Your Boxes?</h2>
            <p className="text-muted-foreground mb-6">
              Join 1,000+ people who never lose track of their stuff
            </p>
            <Button 
              size="lg" 
              className="h-14 px-8 rounded-xl text-base font-semibold bg-[hsl(142,71%,45%)] hover:bg-[hsl(142,71%,40%)] text-white shadow-lg shadow-[hsl(142,71%,45%)]/25"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Scan Your First Box — Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Camera Modal */}
      <CameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
      />
    </div>
  );
}
